import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { emitNewConversation } from '@/lib/eventHandlers';

export const useNewChat = (onChatCreated: () => void, isMobile: boolean, setOpen?: (open: boolean) => void) => {
  const [isNewChatDialogOpen, setIsNewChatDialogOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isBuilderPage = location.pathname.includes('/builder');

  const handleNewChat = async () => {
    setIsNewChatDialogOpen(false);
    
    try {
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      console.log("Creating new chat for user:", user.id);
      
      localStorage.removeItem('currentChatId');
      
      const { data: newChat, error } = await supabase
        .from('chats')
        .insert({
          user_id: user.id,
          title: 'Neue Konversation'
        })
        .select()
        .single();
      
      if (error || !newChat || !newChat.id) {
        throw error || new Error("Failed to create new chat");
      }
      
      console.log("New chat created with ID:", newChat.id);
      
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          chat_id: newChat.id,
          role: 'assistant',
          content: "Hallo! Ich bin Ihr E-Mail-Assistent. Wie kann ich Ihnen heute helfen?",
          context_type: 'email',
          metadata: { 
            initialMessage: true,
            isWelcomeMessage: true,
            messageType: 'system_welcome'
          }
        });
        
      if (messageError) throw messageError;
      
      localStorage.setItem('currentChatId', newChat.id);
      
      localStorage.removeItem('lastGeneratedEmailContent');
      localStorage.removeItem('generatedEmailContent');
      
      window.dispatchEvent(new CustomEvent('start-new-conversation', { 
        detail: { chatId: newChat.id } 
      }));
      
      onChatCreated();
      emitNewConversation(newChat.id);
      
      if (!isBuilderPage) {
        navigate('/builder');
      } else {
        window.location.href = '/builder';
      }
      
      if (isMobile && setOpen) {
        setOpen(false);
      }
      
      toast({
        title: "Neue Konversation gestartet",
        description: "Eine neue E-Mail-Konversation wurde gestartet."
      });
    } catch (error) {
      console.error('Error creating new chat:', error);
      toast({
        title: "Fehler",
        description: "Neue Konversation konnte nicht erstellt werden.",
        variant: "destructive"
      });
    }
  };

  return {
    isNewChatDialogOpen,
    setIsNewChatDialogOpen,
    handleNewChat
  };
};
